import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { CanonicalLessonPracticeProblemDto, CanonicalLessonResponse } from '../../../models';
import { renderLessonMarkdown } from '../../../shared/lesson-markdown.util';

@Component({
  selector: 'app-admin-canonical-lesson-preview',
  standalone: true,
  imports: [CommonModule, MatButtonModule],
  template: `
    <div class="preview" *ngIf="lesson as selected">
      <div class="preview-header">
        <div>
          <h2 class="title">{{ selected.topicName || selected.skillName || selected.skillId }}</h2>
          <p class="meta">
            <span *ngIf="selected.skillName">{{ selected.skillName }} · </span>
            Pack {{ selected.evidencePackIndex ?? 0 }} · v{{ selected.version }} ·
            <span class="pill">{{ selected.status }}</span>
            <span class="pill ai" *ngIf="selected.isFullyAiGenerated">Fully AI</span>
          </p>
          <p class="meta roles" *ngIf="selected.roleNames?.length">
            Roles: {{ selected.roleNames!.join(', ') }}
          </p>
        </div>
        <div class="actions">
          <button
            mat-flat-button
            color="primary"
            type="button"
            *ngIf="selected.status === 'draft'"
            (click)="publish.emit(selected)"
            [disabled]="busy">
            Publish
          </button>
          <button mat-stroked-button type="button" *ngIf="showArchive" (click)="archive.emit(selected)" [disabled]="busy">
            Archive
          </button>
          <button mat-button type="button" (click)="close.emit()" [disabled]="busy">Close</button>
        </div>
      </div>

      <p class="risk" *ngIf="selected.isFullyAiGenerated && selected.riskNote">{{ selected.riskNote }}</p>

      <article class="body" [innerHTML]="renderedBody(selected)"></article>

      <section class="block" *ngIf="practiceProblems(selected).length">
        <h3>Practice problems</h3>
        <ul>
          <li *ngFor="let p of practiceProblems(selected)">
            <a *ngIf="p.url; else plainTitle" [href]="p.url" target="_blank" rel="noreferrer">{{ p.title || p.url }}</a>
            <ng-template #plainTitle>{{ p.title || 'Practice' }}</ng-template>
            <span class="meta" *ngIf="p.estimatedHours"> · {{ p.estimatedHours }}h</span>
          </li>
        </ul>
      </section>

      <section class="block" *ngIf="selected.furtherReading?.length">
        <h3>Further reading</h3>
        <ul>
          <li *ngFor="let item of selected.furtherReading">
            <a [href]="item.url" target="_blank" rel="noreferrer">{{ item.title }}</a>
            <span class="meta"> · {{ item.provider }}</span>
          </li>
        </ul>
      </section>
    </div>
  `,
  styles: [
    `
      .preview {
        display: grid;
        gap: 1rem;
      }
      .preview-header {
        display: flex;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
        align-items: flex-start;
      }
      .title {
        margin: 0;
        font-size: 1.35rem;
      }
      .meta {
        margin: 0.35rem 0 0;
        color: #64748b;
        font-size: 0.875rem;
      }
      .actions {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
      }
      .pill {
        display: inline-block;
        margin-left: 0.35rem;
        padding: 0.1rem 0.55rem;
        border-radius: 999px;
        background: #e0e7ff;
        color: #3730a3;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: capitalize;
      }
      .pill.ai {
        background: #fce7f3;
        color: #9d174d;
      }
      .risk {
        margin: 0;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        background: #fff7ed;
        border: 1px solid #fed7aa;
        color: #9a3412;
      }
      .body {
        max-height: min(70vh, 640px);
        overflow: auto;
        padding: 1rem 1.25rem;
        border-radius: 10px;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        line-height: 1.6;
      }
      .body :where(h1, h2, h3) {
        margin: 1.1rem 0 0.5rem;
        font-weight: 700;
      }
      .body :where(p) {
        margin: 0.65rem 0;
      }
      .body :where(pre) {
        overflow: auto;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        background: #0f172a;
        color: #e2e8f0;
        font-size: 0.85rem;
      }
      .body :where(a) {
        color: #1d4ed8;
      }
      .block h3 {
        margin: 0 0 0.5rem;
        font-size: 1rem;
      }
      .block ul {
        margin: 0;
        padding-left: 1.2rem;
      }
      .block li {
        margin: 0.35rem 0;
      }
    `
  ]
})
export class AdminCanonicalLessonPreviewComponent {
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) lesson!: CanonicalLessonResponse | null;
  @Input() busy = false;
  @Input() showArchive = true;

  @Output() readonly publish = new EventEmitter<CanonicalLessonResponse>();
  @Output() readonly archive = new EventEmitter<CanonicalLessonResponse>();
  @Output() readonly close = new EventEmitter<void>();

  renderedBody(lesson: CanonicalLessonResponse): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(renderLessonMarkdown(lesson.markdownBody ?? ''));
  }

  practiceProblems(lesson: CanonicalLessonResponse): CanonicalLessonPracticeProblemDto[] {
    return lesson.sections?.handsOnExercise?.practiceProblems ?? [];
  }
}